import React, { createContext, useContext, ReactNode } from 'react';
import { useUserMessages } from '@/hooks/useUserMessages';
import type {
  PresenceStatus,
  UserMessage,
  UserConnection,
  UserPresenceMeta,
  UserMessageAttachment,
} from '@/hooks/useUserMessages';
import type { EmojiClickData } from 'emoji-picker-react';

interface UserMessagesContextType {
  messages: UserMessage[];
  connections: UserConnection[];
  loading: boolean;
  activeConversation: string | null;
  setActiveConversation: (userId: string | null) => void;
  sendMessage: (
    recipientId: string,
    content: string,
    attachments?: UserMessageAttachment[]
  ) => Promise<UserMessage | null>;
  toggleReaction: (messageId: string, emojiData: EmojiClickData) => Promise<void>;
  markAsRead: (messageId: string) => Promise<void>;
  requestConnection: (connectedUserId: string) => Promise<UserConnection | null>;
  updateConnectionStatus: (connectionId: string, status: 'accepted' | 'declined') => Promise<void>;
  unreadMessageCount: number;
  pendingConnectionCount: number;
  onlineUsers: Set<string>;
  presenceMeta: Record<string, UserPresenceMeta>;
  selfPresenceStatus: Exclude<PresenceStatus, 'offline'>;
  selfPresenceMessage: string;
  setPresenceStatus: (status: Exclude<PresenceStatus, 'offline'>, statusMessage?: string) => Promise<void>;
}

const UserMessagesContext = createContext<UserMessagesContextType | undefined>(undefined);

export const UserMessagesProvider = ({ children }: { children: ReactNode }) => {
  const userMessages = useUserMessages();

  return <UserMessagesContext.Provider value={userMessages}>{children}</UserMessagesContext.Provider>;
};

export const useUserMessagesContext = () => {
  const context = useContext(UserMessagesContext);
  if (context === undefined) {
    throw new Error('useUserMessagesContext must be used within a UserMessagesProvider');
  }
  return context;
};
